import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Clock, Layers, Building2, PieChart, Zap } from 'lucide-react'
import HierarchyDrillChart from '../components/govdata/HierarchyDrillChart'
import type { DrillMetric } from '../components/govdata/HierarchyDrillChart'
import VendorConcentrationSection from '../components/govdata/VendorConcentrationSection'
import { govdataAPI } from '../services/govdata'

type MetricKey = DrillMetric | 'vendor_concentration'

const METRICS: { key: MetricKey; label: string; icon: typeof Clock }[] = [
  { key: 'speed', label: 'Speed to Contract', icon: Clock },
  { key: 'vehicles', label: 'Contract Vehicles', icon: Layers },
  { key: 'ot_prevalence', label: 'OT Prevalence', icon: Zap },
  { key: 'small_biz', label: 'Small Business', icon: Building2 },
  { key: 'vendor_concentration', label: 'Vendor Concentration', icon: PieChart },
]

const MIN_FISCAL_YEAR = 2024

export default function AcqPerformanceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [fiscalYears, setFiscalYears] = useState<number[]>([])

  const metric = (searchParams.get('metric') as MetricKey) || 'speed'
  const fiscalYear = searchParams.get('fiscal_year')
    ? Number(searchParams.get('fiscal_year'))
    : undefined

  // Fetch available fiscal years once
  useEffect(() => {
    govdataAPI.acqPerformance({ hierarchy_level: 'agency' })
      .then(resp => {
        setFiscalYears(resp.filters.fiscal_years.filter(fy => fy >= MIN_FISCAL_YEAR))
      })
      .catch(() => {})
  }, [])

  const handleFilterChange = useCallback((params: Record<string, string | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined) {
          next.delete(key)
        } else {
          next.set(key, value)
        }
      }
      return next
    })
  }, [setSearchParams])

  const handleMetricChange = (key: MetricKey) => {
    handleFilterChange({ metric: key === 'speed' ? undefined : key })
  }

  return (
    <div className="w-full h-full bg-[#f1f3f5] overflow-auto">
      <div className="max-w-[1600px] mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow px-6 py-4">
          <h1 className="text-xl font-semibold text-gray-900">Acquisition Performance</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Compare DoW departments on key acquisition metrics
          </p>
        </div>

        {/* Metric Selector + Fiscal Year */}
        <div className="bg-white rounded-xl shadow px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {METRICS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => handleMetricChange(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  metric === key
                    ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Fiscal Year Dropdown */}
          <div className="flex items-center gap-3 shrink-0">
            <select
              value={fiscalYear ?? ''}
              onChange={(e) => handleFilterChange({ fiscal_year: e.target.value || undefined })}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {fiscalYears.map((fy) => (
                <option key={fy} value={fy}>FY{fy}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Main Chart */}
        {metric === 'vendor_concentration' ? (
          <VendorConcentrationSection fiscalYear={fiscalYear} />
        ) : (
          <HierarchyDrillChart metric={metric} fiscalYear={fiscalYear} />
        )}
      </div>
    </div>
  )
}
